import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const personalities = {
  guide: { name: "Guide", color: "#14B8A6", bgClass: "bg-teal-100", textClass: "text-teal-600" },
  friend: { name: "Confidence Coach & Growth Coach", color: "#FACC15", bgClass: "bg-yellow-100", textClass: "text-yellow-600" },
  mentor: { name: "Career Coach", color: "#6366F1", bgClass: "bg-indigo-100", textClass: "text-indigo-600" },
  coach: { name: "The Mentor", color: "#EF4444", bgClass: "bg-red-100", textClass: "text-red-600" }
};

export default function History({ user, onLogout }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        params: { limit: 3 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const handleConversationClick = (conv) => {
    navigate(`/chat/${conv.personality}/${conv.id}`);
  };

  const handleDeleteConversation = async (event, conversationId) => {
    event.stopPropagation();

    const shouldDelete = window.confirm("Delete this conversation permanently?");
    if (!shouldDelete) {
      return;
    }

    try {
      setDeletingId(conversationId);
      await axios.delete(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations((prev) => prev.filter((conv) => conv.id !== conversationId));
      toast.success("Conversation deleted");
    } catch (error) {
      toast.error("Failed to delete conversation");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-pink-50" data-testid="history-page">
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate("/dashboard")} variant="ghost" size="sm" className="rounded-full" data-testid="back-to-dashboard-btn">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Button>
              <h1 className="text-2xl font-extrabold text-slate-800">
                Conversation History
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">{user.name}</span>
              <Button onClick={onLogout} variant="outline" size="sm" className="rounded-full" data-testid="history-logout-btn">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            <p className="mt-4 text-slate-600">Loading conversations...</p>
          </div>
        ) : conversations.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-2">No Conversations Yet</h3>
            <p className="text-slate-600 mb-6">Start chatting with a personality to see your history here</p>
            <Button onClick={() => navigate("/dashboard")} className="btn-primary" data-testid="no-conversations-go-dashboard-btn">
              Go to Dashboard
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Showing your 3 most recent conversations to keep loading fast.
            </p>
            {conversations.map((conv, index) => {
              const persona = personalities[conv.personality];
              return (
                <motion.div
                  key={conv.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className="p-6 card-hover cursor-pointer border-2 hover:border-orange-300"
                    onClick={() => handleConversationClick(conv)}
                    data-testid={`conversation-item-${index}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 ${persona.bgClass} ${persona.textClass} rounded-full text-sm font-bold`}>
                            {persona.name}
                          </span>
                          <span className="text-sm text-slate-500">
                            {formatDistanceToNow(new Date(conv.updated_at), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-slate-700 line-clamp-2">{conv.last_message}</p>
                        <p className="text-sm text-slate-500 mt-2">{conv.message_count} messages</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(event) => handleDeleteConversation(event, conv.id)}
                          disabled={deletingId === conv.id}
                          data-testid={`conversation-delete-${index}`}
                        >
                          {deletingId === conv.id ? "Deleting..." : "Delete"}
                        </Button>
                        <svg className="w-6 h-6 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
